import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { SlipContext } from 'context/slipContext';
import { SlipEntry } from 'types/SlipEntry';
import {
  comboSum,
  SYSTEM_MIN_MATCHES,
  systemLabel,
  systemSizesFor,
} from 'helpers/systemBets';
import styles from './SystemPicker.module.css';

/** Combination | System, shared by the desktop slip and the slip page. */
export const useSlipType = () => {
  const { entries, system, setSystem } = useContext(SlipContext);
  const matches = (entries as SlipEntry[]).length;
  return {
    isSystem: system !== null,
    chooseCombination: () => setSystem(null),
    // The biggest system the matches allow is picked to start with (8/9).
    chooseSystem: () =>
      system === null &&
      setSystem(matches >= SYSTEM_MIN_MATCHES ? [matches - 1] : []),
  };
};

/**
 * The sizes of a system ticket: "2/9" .. "8/9", several at once, each with
 * its number of combinations (more with several picks on one match).
 */
export const SystemPicker = () => {
  const { t } = useTranslation();
  const { entries, system, setSystem, betNumber } = useContext(SlipContext);
  if (system === null) return null;
  const list = entries as SlipEntry[];
  const matches = list.length;
  if (matches < SYSTEM_MIN_MATCHES) {
    return (
      <p className={styles.note}>
        {t('system_min_matches', { count: SYSTEM_MIN_MATCHES })}
      </p>
    );
  }
  const counts = list.map((e) =>
    Math.max(e.bets.filter((b) => !b.disabled).length, 1)
  );
  const chosen = system as number[];
  const toggle = (k: number) =>
    setSystem(
      chosen.includes(k) ? chosen.filter((x) => x !== k) : [...chosen, k]
    );
  return (
    <div className={styles.wrapper}>
      <div className={styles.sizes}>
        {systemSizesFor(matches).map((k) => (
          <button
            type="button"
            key={k}
            className={classNames(
              styles.size,
              chosen.includes(k) && styles.active
            )}
            aria-pressed={chosen.includes(k)}
            onClick={() => toggle(k)}
          >
            <strong>
              {k}/{matches}
            </strong>
            <small>{Math.round(comboSum(counts, [k]))}</small>
          </button>
        ))}
      </div>
      <p className={styles.note}>
        {chosen.length > 0
          ? t('system_summary', {
              system: systemLabel(chosen, matches),
              count: betNumber,
              interpolation: { escapeValue: false },
            })
          : t('system_pick_size')}
      </p>
    </div>
  );
};
